/**
 * MUI Selector Component
 * 
 * This component is the Material UI equivalent of the original Selector component.
 * It provides a dropdown selector with label.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'theme/ThemeContext';
import InputLabel from '../atoms/InputLabel';
import Select from '../atoms/Select';
import ReducersIndex from 'reduxModules/ducks';
import { getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { getAreaState } from 'reduxModules/selectors/areaSelectors';
import { MIC_CATEGORIES } from 'types/models/microphone';
import { SelectorProps } from 'types/components/molecules';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * MUI Selector component
 * 
 * @param props - Component props
 * @returns The MUI Selector component
 */
const MuiSelector: React.FC<SelectorProps> = React.memo((props) => {
  const { mode, muiTheme } = useTheme();
  
  // Use default values for filter parameter and function if not provided
  const { parameter = null, filter = null } = props.filter ?? {};
  
  // Helper function to safely call getCurrentValue2 with TypeScript
  const safeGetValue = useCallback((state: any, param: string) => {
    // Cast the function to allow any number of arguments
    const getValue = getCurrentValue2 as any;
    return getValue(state, param);
  }, []);
  
  // Use useRef to track if we're in the middle of updating to prevent loops
  const isUpdatingRef = useRef(false);
  
  // Optimize selector with shallow equality and memoization. `parameter` is normally a
  // single property name/path (resolves to one value), but a filter that needs to react to
  // more than one independent piece of state (e.g. the expansion mic type selector's Devio UCX
  // switch-availability gating — platform, the switch-allowed flag, AND the
  // current expansion type, alongside the pre-existing table-presence check) can instead
  // pass an array of parameter names; each is resolved independently and handed to `filter`
  // as an array, mirroring the multi-parameter `condition.parameter` pattern used elsewhere
  // in menus.js/modals.js.
  const args = useSelector((state: any) => {
    if (!parameter) return null;
    if (Array.isArray(parameter)) {
      return parameter.map((p: string) => safeGetValue(state, p));
    }
    return safeGetValue(state, parameter);
  }, (prev, next) => {
    if (Array.isArray(prev) || Array.isArray(next)) {
      return Array.isArray(prev) && Array.isArray(next) &&
        prev.length === next.length && prev.every((v, i) => v === next[i]);
    }
    return prev === next;
  });
  
  // WDT-90: computed early (before `items` below) so the auto-resolved
  // Product Family value can be reflected in the displayed item list.
  const isProductFamilySelector = Boolean(props.isProductFamily) && props.dispatcher === 'setProductFamily';
  const area = useSelector((state: any) => (isProductFamilySelector ? getAreaState(state) : undefined));

  // Stable memoization of items to prevent recalculation
  const items = useMemo(() => {
    // If no filter function provided, use props.items directly (performance optimization)
    const base = !filter ? (props.items || []) : filter(args, props.items || []);
    // Bar mic (VBC 2500a / VBC 2800) + Microsoft Teams or Zoom Rooms UC Compute
    // + no expansion mics/speakers resolves Product Family to 'Parlé' (see
    // resolveBarProductFamily in utilities/parleBarBundle.js) — a fixed value
    // outside the normal Devio/Tesira choice. Add it to the displayed items so
    // the (disabled) control shows "Parlé" instead of rendering blank for a
    // value that isn't in the base Devio/Tesira list.
    if (isProductFamilySelector && area?.productFamily === 'Parlé' && !base.includes('Parlé')) {
      return [...base, 'Parlé'];
    }
    return base;
  }, [args, props.items, filter, isProductFamilySelector, area?.productFamily]);

  // More specific selector that only watches the exact value needed
  const currentValue = useSelector((state: any) => {
    if (!props.dispatcher) return '';
    const result = safeGetValue(state, props.dispatcher);
    const raw = props.name ? result?.[props.name] : result;
    return props.valueTransform ? props.valueTransform(raw) : raw;
  }, (prev, next) => prev === next);

  const [value, setValue] = useState<string | number>('');
  const dispatch = useDispatch();
  const previousFilterParamRef = useRef(args);
  const previousItemsRef = useRef<any[]>([]);

  // WDT-90 field-order fix: Product Family is now ALWAYS rendered — menus.js
  // no longer hides it for bar systems, oversized layouts, or any other
  // field's value ("all three fields always visible" is the core AC). In the
  // cases where the family is programmatically resolved rather than a real
  // user choice, we disable the control instead of hiding it (same "visible
  // but locked" pattern RadioButtons.tsx uses for Platform):
  //   - Resolved to the fixed bar "Parlé" UC bundle (VBC 2500a/2800 + Teams/
  //     Zoom + no expansion — see resolveBarProductFamily in
  //     utilities/parleBarBundle.js) — no manual choice for that exact shape.
  //     Any OTHER bar design (no UC Compute, CBC/ABC bars, etc.) defaults its
  //     value to 'Tesira' but is NOT locked — Family stays a real, editable
  //     Devio/Tesira choice there, same as a non-bar design.
  //   - Oversized non-bar layouts (>8 primary mics or >16 primary speakers) —
  //     replaces the old size-based visibility condition 1:1.
  //   - Platform = Android, full stop — Android is the Devio UCX (Tree Frog)
  //     path, so Devio is the only valid family (setPlatform/
  //     revalidateUcxEligibility in reduxModules/ducks/room.ts resolve it to
  //     'Devio', or 'Tesira' on the port-overflow fallback); it is never a
  //     free user choice between Devio/Tesira while Platform = Android.
  const familyOversized = Boolean(
    area?.microphones?.layout && area?.speakers?.layout &&
    (area.microphones.layout.length > 8 || area.speakers.layout.length > 16)
  );
  // BMA360D (ceiling-tile mic) is Dante-based and ships on a Tesira DSP only —
  // no Devio SCX support — so Family is pinned to 'Tesira' and disabled,
  // matching the Platform lock to Windows in RadioButtons.tsx. Reducer-side
  // enforcement lives in revalidateUcxEligibility (reduxModules/ducks/room.ts).
  const familyCeilingTileMic = Boolean(area?.microphones?.model) &&
    MIC_CATEGORIES.CEILING_TILE.has(area.microphones.model);
  const familyLocked = isProductFamilySelector && Boolean(area) && (
    area.productFamily === 'Parlé' ||
    familyOversized ||
    area.platform === 'Android' ||
    familyCeilingTileMic
  );

  // Platform = Android is the Devio UCX hub, not the Devio SCX DSP — the
  // 'Devio' family value is shared by both catalog paths, so relabel it here
  // rather than showing the misleading "Devio SCX" bubble while locked to the
  // Android/Tree Frog hardware. The port-overflow fallback's 'Tesira' value
  // keeps its normal label. The bar's fixed 'Parlé' UC bundle value already
  // reads correctly as-is (Select.tsx falls back to the raw item text when no
  // bubble entry exists), so it needs no override here.
  const familyBubbles = useMemo(() => {
    if (!isProductFamilySelector) return props.bubbles;
    if (area?.platform === 'Android') return { ...props.bubbles, Devio: 'Devio UCX' };
    return props.bubbles;
  }, [isProductFamilySelector, area?.platform, area?.productFamily, props.bubbles]);
  
  useEffect(() => {
    // Prevent infinite loops by checking if we're already updating
    if (isUpdatingRef.current) return;

    // currentValue is already the exact value we need (no need for props.name logic)
    const current = currentValue;

    // Check if the filter parameter has changed (indicating a type change)
    const hasFilterParamChanged = args !== previousFilterParamRef.current;
    // More efficient items comparison - only check length first
    const hasItemsChanged = items.length !== previousItemsRef.current.length ||
                           JSON.stringify(items) !== JSON.stringify(previousItemsRef.current);

    // Early return if nothing significant changed AND displayed value already matches Redux state
    if (!hasFilterParamChanged && !hasItemsChanged && current !== undefined && current !== null && current !== '' && current === value) {
      // Update references to prevent future false positives
      previousFilterParamRef.current = args;
      previousItemsRef.current = items;
      return;
    }

    let newValue: string | number = '';

    // Determine what the value should be
    if (current === '' || current === null || current === undefined) {
      newValue = items.length > 0 ? items[0] : '';
    } else if (items.includes(current)) {
      newValue = current;
    } else if ((hasFilterParamChanged || hasItemsChanged) && items.length > 0) {
      // Filter parameter or items changed - set to first available item
      newValue = items[0];
    } else {
      newValue = items.length > 0 ? items[0] : '';
    }

    // Update references
    previousFilterParamRef.current = args;
    previousItemsRef.current = items;

    // Only update local state if the value actually needs to change
    if (value !== newValue) {
      isUpdatingRef.current = true;
      setValue(newValue);

      // Reset the updating flag
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [currentValue, items, args, value]); // Simplified dependencies

  /**
   * Handle change event - memoized to prevent unnecessary re-renders
   */
  const handleChange = useCallback((evt: React.ChangeEvent<HTMLSelectElement>): void => {
    // Prevent loops during updates
    if (isUpdatingRef.current) return;
    
    const newValue = evt.target.value;
    
    // Set updating flag
    isUpdatingRef.current = true;
    
    setValue(newValue);
    
    const payload = props.name ? { [props.name]: newValue } : newValue;
    
    if (props.dispatcher) {
      // Check for action in container reducer
      const actionCreator = ReducersIndex.container[props.dispatcher];
      
      if (actionCreator) {
        // Create a proper Redux action
        const action = {
          type: `container/${props.dispatcher}`,
          payload
        };
        dispatch(action as unknown as AnyAction);
      }
    }
    
    // Reset updating flag after a short delay
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, [props.dispatcher, props.name, dispatch]);

  // Memoize the Select props to prevent unnecessary re-renders.
  // Omit valueTransform — it's a Selector-only prop and must not reach the DOM.
  const selectProps = useMemo(() => {
    const { valueTransform: _vt, isProductFamily: _ipf, ...rest } = props;
    return { ...rest, items, value, onChange: handleChange, disabled: props.disabled || familyLocked, bubbles: familyBubbles };
  }, [props, items, value, handleChange, familyLocked, familyBubbles]);

  return (
    <Box 
      className={`d-flex flex-column ${props.className || ''}`} 
      sx={{ 
        width: '100%',
        '& .MuiSelect-root': {
          backgroundColor: muiTheme.palette.background.paper,
          color: muiTheme.palette.text.primary,
        }
      }}
    >
      {props.label && <InputLabel>{props.label}</InputLabel>}
      <Select {...selectProps} />
    </Box>
  );
});

// Add display name for debugging
MuiSelector.displayName = 'MuiSelector';

export type { SelectorProps };
export default MuiSelector;
